import { PrepareResultDE } from "@visonum/network-quality-sdk";
import * as R from "ramda";
import { MeasurementState, Phase } from "../../features/measurement/measurementSlice";
import { isCustomer } from "./customerUtls";
import t from "../t";

export enum MeasurementPercentageKind {
    ExactInterval,
    PossibleInterval,
    NotApplicable,
}

type MeasurementPercentageInfo = {
    kind: MeasurementPercentageKind.ExactInterval,
    stageValue: number; // 0 .. 1
    interval: number; // in sec
} | {
    kind: MeasurementPercentageKind.PossibleInterval,
    stageValue: number; // 0 .. 1
    possibleInterval: number; // in sec
} | {
    kind: MeasurementPercentageKind.NotApplicable,
}

/* pure */
export const getMeasurementPercentageInfo = (state: MeasurementState): MeasurementPercentageInfo => {
    const BenchmarkingTime = 1;
    const getDownloadingTime = (pr: PrepareResultDE): number => {
        return pr.config.download.duration;
    }
    const getUploadingTime = (pr: PrepareResultDE): number => {
        return pr.config.upload.duration;
    }
    const getPingingTime = (pr: PrepareResultDE): number => {
        return pr.config.ping.duration;
    }
    const isModemTestAvailable = (pr: PrepareResultDE): boolean => {
        return pr.init.cmts?.isModemTestAvailable === true;
    }
    const getModemResolvingTime = (pr: PrepareResultDE) => {
        return isModemTestAvailable(pr) ? 5 : null;
    }
    const getModemExecutingTime = (pr: PrepareResultDE) => {
        return isModemTestAvailable(pr) ? 5 : null;
    }
    const CompletingTime = 0.4;
    const getTotalTime = (pr: PrepareResultDE): number => {
        return R.sum([
            BenchmarkingTime,
            getDownloadingTime(pr),
            getUploadingTime(pr),
            getPingingTime(pr),
            CompletingTime,
            getModemResolvingTime(pr) ?? 0,
            getModemExecutingTime(pr) ?? 0,
            CompletingTime,
        ]);
    }

    const getInfo = (pr: PrepareResultDE, kind: MeasurementPercentageKind, interval: number | null): MeasurementPercentageInfo => {
        if (interval === null) {
            return { kind: MeasurementPercentageKind.NotApplicable }
        }

        switch (kind) {
            case MeasurementPercentageKind.NotApplicable:
                return { kind: MeasurementPercentageKind.NotApplicable }
            case MeasurementPercentageKind.ExactInterval:
                return { kind, interval, stageValue: interval / getTotalTime(pr) }
            case MeasurementPercentageKind.PossibleInterval:
                return { kind, possibleInterval: interval, stageValue: interval / getTotalTime(pr) }
        }
    }

    switch (state.kind) {
        case Phase.Aborting:
        case Phase.NotStarted:
        case Phase.Preparing:
            return { kind: MeasurementPercentageKind.NotApplicable }
        case Phase.Benchmarking:
            return getInfo(state.prepareResult, MeasurementPercentageKind.PossibleInterval, BenchmarkingTime);
        case Phase.Downloading:
            return getInfo(state.prepareResult, MeasurementPercentageKind.ExactInterval, getDownloadingTime(state.prepareResult));
        case Phase.Uploading:
            return getInfo(state.prepareResult, MeasurementPercentageKind.ExactInterval, getUploadingTime(state.prepareResult));
        case Phase.Pinging:
            return getInfo(state.prepareResult, MeasurementPercentageKind.ExactInterval, getPingingTime(state.prepareResult));
        case Phase.Completing:
            return getInfo(state.prepareResult, MeasurementPercentageKind.PossibleInterval, CompletingTime);
        case Phase.ModemResolving:
            return getInfo(state.prepareResult, MeasurementPercentageKind.PossibleInterval, getModemResolvingTime(state.prepareResult));
        case Phase.ModemExecuting:
            return getInfo(state.prepareResult, MeasurementPercentageKind.PossibleInterval, getModemExecutingTime(state.prepareResult));
        case Phase.CompletingNoModem:
        case Phase.CompletingWithModem:
            return getInfo(state.prepareResult, MeasurementPercentageKind.PossibleInterval, CompletingTime);
        case Phase.FinishedNoModem:
        case Phase.FinishedWithModem:
            return { kind: MeasurementPercentageKind.NotApplicable }
    }
}

export const isServerActive = (state: MeasurementState) => {
    switch (state.kind) {
        case Phase.Aborting:
        case Phase.NotStarted:
        case Phase.Preparing:
        case Phase.Benchmarking:
            return false;
        case Phase.Downloading:
        case Phase.Uploading:
        case Phase.Pinging:
        case Phase.Completing:
        case Phase.ModemResolving:
        case Phase.ModemExecuting:
        case Phase.CompletingNoModem:
        case Phase.CompletingWithModem:
            return true;
        case Phase.FinishedNoModem:
        case Phase.FinishedWithModem:
            return false;
    }
}

export const isRouterActive = (state: MeasurementState) => {
    switch (state.kind) {
        case Phase.Aborting:
        case Phase.NotStarted:
        case Phase.Preparing:
        case Phase.Benchmarking:
        case Phase.Downloading:
        case Phase.Uploading:
        case Phase.Pinging:
        case Phase.Completing:
        case Phase.ModemResolving:
            return false;
        case Phase.ModemExecuting:
        case Phase.CompletingNoModem:
        case Phase.CompletingWithModem:
            return isCustomer(state.prepareResult.init.modem);
        case Phase.FinishedNoModem:
        case Phase.FinishedWithModem:
            return false;
    }
}

export const isDeviceActive = (state: MeasurementState) => {
    switch (state.kind) {
        case Phase.Aborting:
        case Phase.NotStarted:
        case Phase.Preparing:
        case Phase.Benchmarking:
            return false;
        case Phase.Downloading:
        case Phase.Uploading:
        case Phase.Pinging:
        case Phase.Completing:
        case Phase.ModemResolving:
            return true;
        case Phase.ModemExecuting:
        case Phase.CompletingNoModem:
        case Phase.CompletingWithModem:
        case Phase.FinishedNoModem:
        case Phase.FinishedWithModem:
            return false;
    }
}

export const infoText = (state: MeasurementState): string => {
    switch (state.kind) {
        case Phase.Aborting:
            return "";
        case Phase.NotStarted:
        case Phase.Preparing:
        case Phase.Benchmarking:
        case Phase.Downloading:
            return t("Deine Messung wird durchgeführt");
        case Phase.Uploading:
            return t("Bitte gedulde Dich noch einen Moment");
        case Phase.Pinging:
            return t("Es dauert nicht mehr lang");
        case Phase.Completing:
        case Phase.ModemResolving:
        case Phase.ModemExecuting:
        case Phase.CompletingNoModem:
        case Phase.CompletingWithModem:
            return t("Fast fertig");
        case Phase.FinishedNoModem:
        case Phase.FinishedWithModem:
            return t("Hier geht’s zu Deinen Ergebnissen");
    }
}

export const isFinished = (state: MeasurementState): boolean =>
    state.kind === Phase.FinishedNoModem || state.kind === Phase.FinishedWithModem;
